import { env } from '@/env'
import { asBoolean, asDate, asNumber, asObject, asRaw, asString, asStringArray } from './guards'
import { Media } from './media'
import { User } from './user'

const STEAM_HEADER = 'https://cdn.akamai.steamstatic.com/steam/apps/{id}/header.jpg'

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Cents -> "$59.99". Every price in the app goes through this. */
export function formatUsdCents(cents: number): string {
  return USD.format(cents / 100)
}

/** Port of constructors/game_info.js */
export class GameInfo {
  id: number | null
  name: string | null
  description: string | null
  genres: string[] | null
  platforms: string[] | null
  price_usd: number | null
  igdb_game_uuid: number | null
  cover_media_id: number | null
  cover_media: Media | null
  landscape_media_id: number | null
  landscape_media: Media | null
  owner_id: number | null
  owner: User | null
  is_searchable: boolean | null
  steam_id: number | null
  /** The game's own site, set by whoever added a custom game. */
  website: string | null
  /** Featured video link (YouTube or Vimeo), shown on its giveaways. */
  video_url: string | null
  /** Ordered images ("media:<id>") and video links. See lib/gallery. */
  gallery: string[]

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.name = asString(d.name)
    this.description = asString(d.description)
    this.genres = asStringArray(d.genres)
    this.platforms = asStringArray(d.platforms)
    this.price_usd = asNumber(d.price_usd)
    this.igdb_game_uuid = asNumber(d.igdb_game_uuid)
    this.cover_media_id = asNumber(d.cover_media_id)
    this.cover_media = asObject(d.cover_media) ? new Media(d.cover_media) : null
    this.landscape_media_id = asNumber(d.landscape_media_id)
    this.landscape_media = asObject(d.landscape_media) ? new Media(d.landscape_media) : null
    this.owner_id = asNumber(d.owner_id)
    this.owner = asObject(d.owner) ? new User(d.owner) : null
    this.is_searchable = asBoolean(d.is_searchable)
    this.steam_id = asNumber(d.steam_id)
    this.website = asString(d.website)
    this.video_url = asString(d.video_url)
    this.gallery = asStringArray(d.gallery) ?? []
  }

  /** Platforms and genres flattened into one chip list. */
  get tags(): string[] {
    return [...(this.platforms ?? []), ...(this.genres ?? [])]
  }

  get display_landscape_media(): string {
    if (this.steam_id) return STEAM_HEADER.replace('{id}', String(this.steam_id))
    return this.landscape_media?.src ?? this.cover_media?.src ?? ''
  }

  get display_cover_media(): string {
    if (this.cover_media_id)
      return `${env.apiBaseUrl}/media/${this.cover_media_id}/display/landscape`
    if (this.steam_id) return STEAM_HEADER.replace('{id}', String(this.steam_id))
    return this.landscape_media?.src ?? ''
  }

  /**
   * A small square of the cover, for search results. display_cover_media is
   * cropped to 16:9 for wide cards; cropping that again to a square left only
   * a thin strip from the middle of a portrait cover.
   */
  get display_cover_thumbnail(): string {
    if (this.cover_media_id)
      return `${env.apiBaseUrl}/media/${this.cover_media_id}/display/avatar`
    return this.display_cover_media
  }

  /**
   * price_usd is stored in cents and is always rendered to two decimal places:
   * the original divided by 100 and interpolated the result, so a $60 game
   * showed as "$60" while a $59.99 one showed as "$59.99".
   *
   * The 9900-cent fallback is the original's behaviour for an unpriced game.
   * It deliberately triggers on 0 as well as on null: the API uses 0 for "no
   * retail price recorded", and a giveaway is not worth $0.00 — that reads as
   * the prize being worthless rather than as the value being unknown.
   */
  get display_price(): string {
    return formatUsdCents(this.price_usd || 9900)
  }
}

export class GameInfoWebsite {
  id: number | null
  game_info_id: number | null
  url: string | null
  category: string | null
  igdb_game_uuid: number | null
  igdb_website_uuid: number | null
  created_at: Date | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.game_info_id = asNumber(d.game_info_id)
    this.url = asString(d.url)
    this.category = asString(d.category)
    this.igdb_game_uuid = asNumber(d.igdb_game_uuid)
    this.igdb_website_uuid = asNumber(d.igdb_website_uuid)
    this.created_at = asDate(d.created_at)
  }

  get steam_id(): number | null {
    if (this.category !== 'steam' || !this.url) return null
    const id = Number(this.url.replace('https://store.steampowered.com/app/', ''))
    return Number.isFinite(id) ? id : null
  }
}
